import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { Pressable, Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'

import { cn } from '@/utils/cn'
import { useToast } from '@/components/base'
import { Spinner } from '@/components/icon'

const MAX_BLOCKING_DURATION_MS = 45_000

interface LoadingIndicatorState {
  start: (text?: string) => void
  stop: () => void
  isLoading: boolean
}

const LoadingIndicatorContext = createContext<LoadingIndicatorState>({
  start: () => undefined,
  stop: () => undefined,
  isLoading: false
})

export function LoadingIndicatorProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const toast = useToast()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [text, setText] = useState<string | null>(null)

  const start = useCallback((text?: string) => {
    setIsLoading(true)
    setText(text ?? null)
  }, [])
  const stop = useCallback(() => {
    setIsLoading(false)
    setText(null)
  }, [])

  useEffect(() => {
    if (!isLoading) return

    const timeout = setTimeout(() => {
      console.error(`Blocking loading indicator timed out after ${MAX_BLOCKING_DURATION_MS}ms`)
      stop()
      toast.show({ variant: 'error', description: t('ui:operationTimedOut') })
    }, MAX_BLOCKING_DURATION_MS)

    return () => clearTimeout(timeout)
  }, [isLoading, stop, t, toast])

  return (
    <LoadingIndicatorContext.Provider value={{ start, stop, isLoading }}>
      {children}
      {isLoading && <LoadingIndicator text={text} />}
    </LoadingIndicatorContext.Provider>
  )
}

export function useLoadingIndicator() {
  return useContext(LoadingIndicatorContext)
}

interface LoadingIndicatorProps {
  text: string | null
}

function LoadingIndicator({ text }: LoadingIndicatorProps) {
  return (
    <Pressable className={'absolute z-10 size-full items-center justify-center'}>
      <View
        className={cn(
          'items-center gap-y-3 rounded-lg bg-black/80',
          text ? 'h-[108px] w-[104px]' : 'size-[100px] justify-center'
        )}
      >
        <View className={cn('size-9 animate-spin items-center justify-center', text && 'mt-5')}>
          <Spinner className={'size-9 text-white'} />
        </View>
        {text && <Text className={'text-base text-text-onColor'}>{text}</Text>}
      </View>
    </Pressable>
  )
}
